<template>
    <div class="content-wrapper shifts-scheduling-page">
        <div class="row align-items-start mb-4">
            <div class="col-lg-8">
                <app-breadcrumb :page-title="$t('shifts_and_scheduling')" :icon="'calendar'" />
                <p class="shifts-scheduling-page__intro mb-0">
                    {{ $t('shifts_and_scheduling_intro') }}
                </p>
            </div>
            <div class="col-lg-4 mt-3 mt-lg-0">
                <div class="shifts-scheduling-page__top-actions">
                    <button
                        v-if="canCreateShifts"
                        type="button"
                        class="btn btn-primary btn-with-shadow"
                        @click="startCreatingShift()"
                    >
                        {{ $t('create_shift') }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        @click="refreshTable"
                    >
                        {{ $t('refresh') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="card card-with-shadow border-0 mb-4">
            <div class="card-body shifts-scheduling-page__table-card-body">
                <app-table :id="tableId" :options="options" @action="getListAction" />
            </div>
        </div>

        <transition name="shift-editor-fade">
            <div v-if="editorOpen" class="shifts-scheduling-page__editor-shell">
                <button
                    type="button"
                    class="shifts-scheduling-page__editor-backdrop"
                    aria-label="Close shift editor"
                    @click="closeEditor"
                />

                <div class="shifts-scheduling-page__editor-drawer">
                    <div class="shifts-scheduling-page__editor-drawer-header">
                        <div>
                            <h4 class="mb-1">{{ isEditingShift ? $t('edit_shift') : $t('create_shift') }}</h4>
                            <p class="text-muted mb-0">
                                {{ $t('shift_editor_hint') }}
                            </p>
                        </div>
                        <button
                            type="button"
                            class="btn btn-link p-0 text-muted"
                            @click="closeEditor"
                        >
                            {{ $t('cancel') }}
                        </button>
                    </div>

                    <div class="shifts-scheduling-page__editor-drawer-body position-relative">
                        <div v-if="formLoading" class="shifts-scheduling-page__panel-loading">
                            <app-overlay-loader />
                        </div>

                        <div class="row">
                            <div class="col-12 mb-3">
                                <label class="shifts-scheduling-page__label">{{ $t('name') }}</label>
                                <app-input
                                    id="shift_form_name"
                                    v-model="shiftForm.name"
                                    type="text"
                                    :disabled="!canEditCurrentShift"
                                    :placeholder="$t('name')"
                                />
                                <small v-if="fieldError('name')" class="text-danger validation-error">{{ fieldError('name') }}</small>
                            </div>
                            <div class="col-12 mb-3">
                                <label class="shifts-scheduling-page__label">{{ $t('location') }}</label>
                                <app-input
                                    id="shift_form_location"
                                    v-model="shiftForm.location_id"
                                    :list="locationOptions"
                                    list-value-field="name"
                                    :disabled="!canEditCurrentShift"
                                    :placeholder="$t('choose_one')"
                                    type="search-select"
                                />
                                <small v-if="fieldError('location_id')" class="text-danger validation-error">{{ fieldError('location_id') }}</small>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="shifts-scheduling-page__label">{{ $t('start_date') }}</label>
                                <app-input
                                    id="shift_form_start_date"
                                    v-model="shiftForm.start_date"
                                    type="date"
                                    :disabled="!canEditCurrentShift"
                                />
                                <small v-if="fieldError('start_date')" class="text-danger validation-error">{{ fieldError('start_date') }}</small>
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="shifts-scheduling-page__label">{{ $t('end_date') }}</label>
                                <app-input
                                    id="shift_form_end_date"
                                    v-model="shiftForm.end_date"
                                    type="date"
                                    :disabled="!canEditCurrentShift"
                                />
                                <small v-if="fieldError('end_date')" class="text-danger validation-error">{{ fieldError('end_date') }}</small>
                            </div>
                        </div>

                        <div class="shifts-scheduling-page__toggle-row mb-3">
                            <button
                                type="button"
                                class="shifts-scheduling-page__toggle-btn"
                                :class="{ 'is-active': shiftForm.type === 'regular' }"
                                :disabled="!canEditCurrentShift"
                                @click="setShiftType('regular')"
                            >
                                <span>{{ $t('regular') }}</span>
                                <small>{{ $t('simple_regular_shift') }}</small>
                            </button>
                            <button
                                type="button"
                                class="shifts-scheduling-page__toggle-btn"
                                :class="{ 'is-active': shiftForm.type === 'scheduled' }"
                                :disabled="!canEditCurrentShift"
                                @click="setShiftType('scheduled')"
                            >
                                <span>{{ $t('scheduled') }}</span>
                                <small>{{ $t('custom_scheduled_shift') }}</small>
                            </button>
                        </div>

                        <div v-if="shiftForm.type === 'regular'">
                            <div class="row">
                                <div class="col-md-6 mb-3">
                                    <label class="shifts-scheduling-page__label">{{ $t('start_time') }}</label>
                                    <app-input
                                        id="shift_form_start_at"
                                        v-model="shiftForm.start_at"
                                        type="time"
                                        :disabled="!canEditCurrentShift"
                                        :placeholder="$t('start_time')"
                                    />
                                    <small v-if="fieldError('start_at')" class="text-danger validation-error">{{ fieldError('start_at') }}</small>
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label class="shifts-scheduling-page__label">{{ $t('end_time') }}</label>
                                    <app-input
                                        id="shift_form_end_at"
                                        v-model="shiftForm.end_at"
                                        type="time"
                                        :disabled="!canEditCurrentShift"
                                        :placeholder="$t('end_time')"
                                    />
                                    <small v-if="fieldError('end_at')" class="text-danger validation-error">{{ fieldError('end_at') }}</small>
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label class="shifts-scheduling-page__label d-inline-flex align-items-center">
                                        <span>{{ $t('break_minutes') }}</span>
                                        <span
                                            class="shifts-scheduling-page__help-trigger"
                                            data-toggle="tooltip"
                                            data-placement="top"
                                            :title="$t('break_minutes_help')"
                                        >
                                            <app-icon name="help-circle" />
                                        </span>
                                    </label>
                                    <app-input
                                        id="shift_form_break_minutes"
                                        v-model="shiftForm.break_minutes"
                                        type="number"
                                        min="0"
                                        :disabled="!canEditCurrentShift"
                                        :placeholder="$t('break_minutes')"
                                    />
                                    <small v-if="fieldError('break_minutes')" class="text-danger validation-error">{{ fieldError('break_minutes') }}</small>
                                </div>
                                <div class="col-md-6 mb-3">
                                    <label class="shifts-scheduling-page__label d-inline-flex align-items-center">
                                        <span>{{ $t('grace_minutes') }}</span>
                                        <span
                                            class="shifts-scheduling-page__help-trigger"
                                            data-toggle="tooltip"
                                            data-placement="top"
                                            :title="$t('grace_minutes_help')"
                                        >
                                            <app-icon name="help-circle" />
                                        </span>
                                    </label>
                                    <app-input
                                        id="shift_form_grace_minutes"
                                        v-model="shiftForm.grace_minutes"
                                        type="number"
                                        min="0"
                                        :disabled="!canEditCurrentShift"
                                        :placeholder="$t('grace_minutes')"
                                    />
                                    <small v-if="fieldError('grace_minutes')" class="text-danger validation-error">{{ fieldError('grace_minutes') }}</small>
                                </div>
                            </div>

                            <div class="mb-3">
                                <label class="shifts-scheduling-page__label">{{ $t('weekend_days') }}</label>
                                <app-input
                                    type="checkbox"
                                    :list="weekdayOptions"
                                    list-value-field="value"
                                    v-model="shiftForm.weekend_days"
                                    radio-checkbox-wrapper="row"
                                    custom-checkbox-type="checkbox-default col-md-4 col-lg-4"
                                    :disabled="!canEditCurrentShift"
                                />
                            </div>
                        </div>

                        <div v-else>
                            <div class="shifts-scheduling-page__scheduled-grid-head mb-3">
                                <div>
                                    <label class="shifts-scheduling-page__label mb-1">{{ $t('scheduled_days') }}</label>
                                    <p class="text-muted mb-0">{{ $t('scheduled_days_hint') }}</p>
                                    <p class="text-muted mb-0 mt-1">{{ $t('weekend_shift_hint') }}</p>
                                </div>
                                <span class="badge badge-pill badge-light">
                                    {{ scheduledWorkingDaysCount }} {{ $t('working_day') }}
                                </span>
                            </div>

                            <div class="shifts-scheduling-page__scheduled-tools mb-3">
                                <div class="shifts-scheduling-page__scheduled-tools-grid">
                                    <app-input
                                        id="scheduled_template_start_at"
                                        v-model="scheduledTemplate.start_at"
                                        type="time"
                                        :disabled="!canEditCurrentShift"
                                        :placeholder="$t('start_time')"
                                    />
                                    <app-input
                                        id="scheduled_template_end_at"
                                        v-model="scheduledTemplate.end_at"
                                        type="time"
                                        :disabled="!canEditCurrentShift"
                                        :placeholder="$t('end_time')"
                                    />
                                    <app-input
                                        id="scheduled_template_break_minutes"
                                        v-model="scheduledTemplate.break_minutes"
                                        type="number"
                                        min="0"
                                        :disabled="!canEditCurrentShift"
                                        :placeholder="$t('break_minutes')"
                                    />
                                    <app-input
                                        id="scheduled_template_grace_minutes"
                                        v-model="scheduledTemplate.grace_minutes"
                                        type="number"
                                        min="0"
                                        :disabled="!canEditCurrentShift"
                                        :placeholder="$t('grace_minutes')"
                                    />
                                </div>
                                <div class="shifts-scheduling-page__scheduled-tools-hint">
                                    <span
                                        class="shifts-scheduling-page__help-inline"
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        :title="$t('break_minutes_help')"
                                    >
                                        <app-icon name="help-circle" />
                                        {{ $t('break_minutes') }}
                                    </span>
                                    <span
                                        class="shifts-scheduling-page__help-inline"
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        :title="$t('grace_minutes_help')"
                                    >
                                        <app-icon name="help-circle" />
                                        {{ $t('grace_minutes') }}
                                    </span>
                                </div>
                                <div class="shifts-scheduling-page__scheduled-tools-actions">
                                    <button
                                        type="button"
                                        class="btn btn-primary btn-sm"
                                        :disabled="!canEditCurrentShift"
                                        @click="applyScheduledTemplateToWorkingDays"
                                    >
                                        {{ $t('apply_to_working_days') }}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-outline-secondary btn-sm"
                                        :disabled="!canEditCurrentShift"
                                        @click="resetScheduledDetails"
                                    >
                                        {{ $t('reset_week_schedule') }}
                                    </button>
                                </div>
                            </div>

                            <div class="row shifts-scheduling-page__scheduled-grid">
                                <div
                                    v-for="(detail, index) in shiftForm.details"
                                    :key="detail.weekday"
                                    class="col-12 mb-3 shifts-scheduling-page__scheduled-day-col"
                                >
                                    <div class="shifts-scheduling-page__scheduled-day">
                                        <div class="d-flex align-items-start justify-content-between mb-2">
                                            <div class="pr-3">
                                                <strong>{{ $t(detail.weekday) }}</strong>
                                                <p class="text-muted mb-0 mt-1 shifts-scheduling-page__scheduled-day-caption">
                                                    {{ detail.is_weekend ? $t('day_off') : $t('working_day') }}
                                                </p>
                                            </div>
                                            <div class="shifts-scheduling-page__day-state-toggle">
                                                <button
                                                    type="button"
                                                    class="btn btn-sm"
                                                    :class="detail.is_weekend ? 'btn-outline-secondary' : 'btn-success'"
                                                    :disabled="!canEditCurrentShift"
                                                    @click="setDetailWorkingState(index, true)"
                                                >
                                                    {{ $t('working_day') }}
                                                </button>
                                                <button
                                                    type="button"
                                                    class="btn btn-sm"
                                                    :class="detail.is_weekend ? 'btn-outline-danger' : 'btn-outline-secondary'"
                                                    :disabled="!canEditCurrentShift"
                                                    @click="setDetailWorkingState(index, false)"
                                                >
                                                    {{ $t('day_off') }}
                                                </button>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <div class="col-6 mb-2">
                                                <app-input
                                                    :id="`scheduled_start_${detail.weekday}`"
                                                    v-model="detail.start_at"
                                                    type="time"
                                                    :disabled="!canEditCurrentShift || detail.is_weekend"
                                                    :placeholder="$t('start_time')"
                                                />
                                                <small v-if="detailFieldError(index, 'start_at')" class="text-danger validation-error">{{ detailFieldError(index, 'start_at') }}</small>
                                            </div>
                                            <div class="col-6 mb-2">
                                                <app-input
                                                    :id="`scheduled_end_${detail.weekday}`"
                                                    v-model="detail.end_at"
                                                    type="time"
                                                    :disabled="!canEditCurrentShift || detail.is_weekend"
                                                    :placeholder="$t('end_time')"
                                                />
                                                <small v-if="detailFieldError(index, 'end_at')" class="text-danger validation-error">{{ detailFieldError(index, 'end_at') }}</small>
                                            </div>
                                            <div class="col-6 mb-2">
                                                <app-input
                                                    :id="`scheduled_break_${detail.weekday}`"
                                                    v-model="detail.break_minutes"
                                                    type="number"
                                                    min="0"
                                                    :disabled="!canEditCurrentShift || detail.is_weekend"
                                                    :placeholder="$t('break_minutes')"
                                                />
                                                <small v-if="detailFieldError(index, 'break_minutes')" class="text-danger validation-error">{{ detailFieldError(index, 'break_minutes') }}</small>
                                            </div>
                                            <div class="col-6 mb-2">
                                                <app-input
                                                    :id="`scheduled_grace_${detail.weekday}`"
                                                    v-model="detail.grace_minutes"
                                                    type="number"
                                                    min="0"
                                                    :disabled="!canEditCurrentShift || detail.is_weekend"
                                                    :placeholder="$t('grace_minutes')"
                                                />
                                                <small v-if="detailFieldError(index, 'grace_minutes')" class="text-danger validation-error">{{ detailFieldError(index, 'grace_minutes') }}</small>
                                            </div>
                                        </div>
                                        <div class="shifts-scheduling-page__scheduled-day-help">
                                            <span
                                                class="shifts-scheduling-page__help-inline"
                                                data-toggle="tooltip"
                                                data-placement="top"
                                                :title="$t('day_configuration_help')"
                                            >
                                                <app-icon name="help-circle" />
                                                {{ $t('day_configuration') }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="shifts-scheduling-page__switches mt-3">
                            <label class="customized-checkbox checkbox-default mb-0">
                                <input
                                    type="checkbox"
                                    :disabled="!canEditCurrentShift"
                                    :checked="shiftForm.is_active"
                                    @change="shiftForm.is_active = $event.target.checked"
                                />
                                <span class="outside"><span class="inside"></span></span>
                                {{ $t('active_shift') }}
                            </label>
                        </div>

                        <div class="shifts-scheduling-page__editor-actions mt-4">
                            <button
                                type="button"
                                class="btn btn-secondary"
                                @click="closeEditor"
                            >
                                {{ $t('cancel') }}
                            </button>
                            <button
                                type="button"
                                class="btn btn-primary btn-with-shadow"
                                :disabled="!canEditCurrentShift || saveLoading"
                                @click="submitShift"
                            >
                                {{ isEditingShift ? $t('update') : $t('save') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="shift-editor-fade">
            <div v-if="assignmentOpen" class="shifts-scheduling-page__editor-shell">
                <button
                    type="button"
                    class="shifts-scheduling-page__editor-backdrop"
                    aria-label="Close shift assignment"
                    @click="closeAssignmentDrawer"
                />

                <div class="shifts-scheduling-page__editor-drawer shifts-scheduling-page__assignment-drawer">
                    <div class="shifts-scheduling-page__editor-drawer-header">
                        <div>
                            <h4 class="mb-1">{{ $t('assign_employees') }}</h4>
                            <p class="text-muted mb-0">{{ $t('assign_employees_hint') }}</p>
                        </div>
                        <button
                            type="button"
                            class="btn btn-link p-0 text-muted"
                            @click="closeAssignmentDrawer"
                        >
                            {{ $t('cancel') }}
                        </button>
                    </div>

                    <div class="shifts-scheduling-page__editor-drawer-body position-relative">
                        <div v-if="assignmentLoading" class="shifts-scheduling-page__panel-loading">
                            <app-overlay-loader />
                        </div>

                        <div class="card card-with-shadow border-0 mb-4">
                            <div class="card-body p-primary">
                                <div class="d-flex align-items-start justify-content-between gap-3">
                                    <div>
                                        <h6 class="mb-1">{{ selectedShiftRecord?.name }}</h6>
                                        <p class="text-muted mb-1">
                                            {{ selectedShiftRecord?.location?.name || $t('unassigned_location') }}
                                        </p>
                                        <small class="text-muted">{{ shiftTimeSummary(selectedShiftRecord) }}</small>
                                    </div>
                                    <span class="badge badge-pill badge-light shifts-scheduling-page__count-pill">
                                        {{ shiftEmployeeCount(selectedShiftRecord) }} {{ $t('employees') }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="mb-3">
                            <label class="shifts-scheduling-page__label">{{ $t('employees') }}</label>
                            <app-input
                                id="shift_assignment_employees"
                                v-model="assignmentForm.employee_ids"
                                :list="assignableEmployeeOptions"
                                list-value-field="name"
                                type="multi-select"
                                :disabled="assignmentLoading"
                                :placeholder="$t('add')"
                            />
                            <small v-if="assignmentFieldError('employee_ids')" class="text-danger validation-error">{{ assignmentFieldError('employee_ids') }}</small>
                        </div>

                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <label class="shifts-scheduling-page__label d-inline-flex align-items-center">
                                    <span>{{ $t('effective_from') }}</span>
                                    <span
                                        class="shifts-scheduling-page__help-trigger"
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        :title="$t('effective_from_help')"
                                    >
                                        <app-icon name="help-circle" />
                                    </span>
                                </label>
                                <app-input
                                    id="shift_assignment_effective_from"
                                    v-model="assignmentForm.effective_from"
                                    type="date"
                                    :disabled="assignmentLoading"
                                />
                            </div>
                            <div class="col-md-6 mb-3">
                                <label class="shifts-scheduling-page__label">{{ $t('notes') }}</label>
                                <app-input
                                    id="shift_assignment_notes"
                                    v-model="assignmentForm.notes"
                                    type="text"
                                    :disabled="assignmentLoading"
                                    :placeholder="$t('notes')"
                                />
                            </div>
                        </div>

                        <label class="customized-checkbox checkbox-default mb-3">
                            <input
                                type="checkbox"
                                :checked="assignmentForm.sync_default_location"
                                :disabled="assignmentLoading"
                                @change="assignmentForm.sync_default_location = $event.target.checked"
                            />
                            <span class="outside"><span class="inside"></span></span>
                            <span class="d-inline-flex align-items-center">
                                <span>{{ $t('sync_default_location') }}</span>
                                <span
                                    class="shifts-scheduling-page__help-trigger"
                                    data-toggle="tooltip"
                                    data-placement="top"
                                    :title="$t('sync_default_location_help')"
                                >
                                    <app-icon name="help-circle" />
                                </span>
                            </span>
                        </label>

                        <button
                            type="button"
                            class="btn btn-primary btn-block btn-with-shadow"
                            :disabled="assignmentLoading || !assignmentForm.employee_ids.length"
                            @click="assignEmployeesToShift"
                        >
                            {{ $t('assign_selected_employees') }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import moment from 'moment';
import { mapState } from 'vuex';
import { WORKING_SHIFT, SHIFT_SCHEDULING } from '../../../Config/ApiUrl';

const defaultWeekdayDefinitions = () => [
    { weekday: 'mon', value: 'monday' },
    { weekday: 'tue', value: 'tuesday' },
    { weekday: 'wed', value: 'wednesday' },
    { weekday: 'thu', value: 'thursday' },
    { weekday: 'fri', value: 'friday' },
    { weekday: 'sat', value: 'saturday' },
    { weekday: 'sun', value: 'sunday' },
];

const defaultScheduledDetails = () => [
    { weekday: 'mon', start_at: '09:00', end_at: '17:00', break_minutes: 60, grace_minutes: 15, is_weekend: false },
    { weekday: 'tue', start_at: '09:00', end_at: '17:00', break_minutes: 60, grace_minutes: 15, is_weekend: false },
    { weekday: 'wed', start_at: '09:00', end_at: '17:00', break_minutes: 60, grace_minutes: 15, is_weekend: false },
    { weekday: 'thu', start_at: '09:00', end_at: '17:00', break_minutes: 60, grace_minutes: 15, is_weekend: false },
    { weekday: 'fri', start_at: '09:00', end_at: '17:00', break_minutes: 60, grace_minutes: 15, is_weekend: false },
    { weekday: 'sat', start_at: '', end_at: '', break_minutes: 0, grace_minutes: 0, is_weekend: true },
    { weekday: 'sun', start_at: '', end_at: '', break_minutes: 0, grace_minutes: 0, is_weekend: true },
];

const defaultScheduledTemplate = () => ({
    start_at: '09:00',
    end_at: '17:00',
    break_minutes: 60,
    grace_minutes: 15,
});

const defaultRegularWeekends = () => ['sat', 'sun'];

export default {
    name: 'AppWorkingShifts',
    extends: CoreLibrary,
    data() {
        return {
            tableId: 'working-shifts-table',
            formLoading: false,
            saveLoading: false,
            assignmentLoading: false,
            editorOpen: false,
            assignmentOpen: false,
            editorShiftId: null,
            selectedShiftId: null,
            selectedShiftRecord: null,
            shiftForm: {},
            scheduledTemplate: defaultScheduledTemplate(),
            formErrors: {},
            assignmentErrors: {},
            assignmentForm: {
                employee_ids: [],
                effective_from: new Date(),
                notes: '',
                sync_default_location: true,
            },
            options: {
                name: 'AppWorkingShifts',
                url: WORKING_SHIFT,
                showHeader: true,
                tableShadow: false,
                enableHighlights: true,
                enableRowSelect: false,
                enableSaveFilter: true,
                columns: [
                    {
                        title: this.$t('#'),
                        type: 'text',
                        key: 'id',
                    },
                    {
                        title: this.$t('shift'),
                        type: 'custom-html',
                        key: 'name',
                        modifier: (value, row) => `
                            <div class="d-flex flex-column">
                                <strong>${this.escapeHtml(row.name)}</strong>
                                <small class="text-muted">${this.escapeHtml(this.shiftTimeSummary(row))}</small>
                            </div>
                        `,
                    },
                    {
                        title: this.$t('type'),
                        type: 'custom-html',
                        key: 'type',
                        modifier: (value, row) => `
                            <span class="badge rounded-pill ${row.type === 'scheduled' ? 'badge-primary' : 'badge-secondary'}">
                                ${this.escapeHtml(this.shiftTypeLabel(row))}
                            </span>
                        `,
                    },
                    {
                        title: this.$t('location'),
                        type: 'custom-html',
                        key: 'location_id',
                        modifier: (value, row) => `
                            <span class="badge rounded-pill badge-light">${this.escapeHtml(row.location?.name || this.$t('unassigned_location'))}</span>
                        `,
                    },
                    {
                        title: this.$t('employees'),
                        type: 'text',
                        key: 'employees_count',
                        modifier: (value, row) => `${this.shiftEmployeeCount(row)}`,
                    },
                    // {
                    //     title: this.$t('date'),
                    //     type: 'custom-html',
                    //     key: 'start_date',
                    //     modifier: (value, row) => this.escapeHtml(this.formatDateRange(row.start_date, row.end_date)),
                    // },
                    {
                        title: this.$t('status'),
                        type: 'custom-html',
                        key: 'is_active',
                        modifier: (value, row) => `
                            <span class="badge rounded-pill ${row.is_active ? 'badge-success' : 'badge-danger'}">
                                ${row.is_active ? this.escapeHtml(this.$t('active')) : this.escapeHtml(this.$t('inactive'))}
                            </span>
                        `,
                    },
                    {
                        title: this.$t('actions'),
                        type: 'action',
                    },
                ],
                actions: [
                    {
                        key: 'assign',
                        title: this.$t('assign_employees'),
                        icon: 'users',
                        type: 'none',
                        modifier: () => this.canAssignEmployees,
                    },
                    {
                        key: 'edit',
                        title: this.$t('edit'),
                        icon: 'edit',
                        type: 'none',
                        modifier: () => this.canUpdateShifts,
                    },
                ],
                filters: [
                    {
                        title: this.$t('date'),
                        type: 'range-picker',
                        key: 'date_range',
                        option: ['today', 'thisMonth', 'lastMonth', 'thisYear', 'lastYear'],
                    },
                    {
                        title: this.$t('status'),
                        type: 'drop-down-filter',
                        key: 'status',
                        option: [
                            { id: 'active', value: this.$t('active') },
                            { id: 'inactive', value: this.$t('inactive') },
                            { id: 'regular', value: this.$t('regular') },
                            { id: 'scheduled', value: this.$t('scheduled') },
                            { id: 'upcoming', value: this.$t('upcoming') },
                            { id: 'expired', value: this.$t('expired') },
                        ],
                    },
                    {
                        title: this.$t('location'),
                        type: 'drop-down-filter',
                        key: 'location_id',
                        option: [],
                        listValueField: 'name',
                    },
                    {
                        title: this.$t('employee'),
                        type: 'drop-down-filter',
                        key: 'employee_id',
                        option: [],
                        listValueField: 'name',
                    },
                    {
                        title: this.$t('department'),
                        type: 'drop-down-filter',
                        key: 'department_id',
                        option: [],
                        listValueField: 'name',
                    },
                ],
                showFilter: true,
                showSearch: true,
                showClearFilter: true,
                paginationType: 'pagination',
                responsive: true,
                rowLimit: 10,
                showAction: true,
                orderBy: 'desc',
                actionType: 'default',
                afterRequestSuccess: (response) => this.handleTableSuccess(response),
                afterRequestError: (response) => this.handleTableError(response),
            },
        };
    },
    computed: {
        ...mapState({
            locationList: state => state.selects.locationList,
            departmentList: state => state.selects.departmentList,
            employeeList: state => state.selects.employeeList,
        }),
        canCreateShifts() {
            return this.$can('create_working_shifts');
        },
        canUpdateShifts() {
            return this.$can('update_working_shifts');
        },
        canAssignEmployees() {
            return this.$can('create_employee_shift_assignments') || this.$can('update_working_shifts');
        },
        canEditCurrentShift() {
            return this.isEditingShift ? this.canUpdateShifts : this.canCreateShifts;
        },
        isEditingShift() {
            return !!this.editorShiftId;
        },
        locationOptions() {
            return Array.isArray(this.locationList) ? this.locationList : [];
        },
        departmentOptions() {
            return Array.isArray(this.departmentList) ? this.departmentList : [];
        },
        employeeOptions() {
            return Array.isArray(this.employeeList)
                ? this.employeeList.map(employee => ({
                    id: employee.id,
                    name: employee.name,
                    department_id: employee.department_id,
                    department_name: employee.department_name,
                }))
                : [];
        },
        assignableEmployeeOptions() {
            return this.employeeOptions;
        },
        scheduledWorkingDaysCount() {
            return Array.isArray(this.shiftForm.details)
                ? this.shiftForm.details.filter(detail => !detail.is_weekend).length
                : 0;
        },
        weekdayOptions() {
            return defaultWeekdayDefinitions().map(day => ({
                id: day.weekday,
                value: this.$t(day.value),
            }));
        },
    },
    watch: {
        locationList: 'syncFilterOptions',
        departmentList: 'syncFilterOptions',
        employeeList: 'syncFilterOptions',
    },
    created() {
        this.shiftForm = this.getInitialShiftForm();
        this.$store.dispatch('getLocations');
        this.$store.dispatch('getDepartments');
        this.$store.dispatch('getEmployees');
        this.syncFilterOptions();
    },
    methods: {
        syncFilterOptions() {
            this.$set(this.options.filters[2], 'option', this.locationOptions);
            this.$set(this.options.filters[3], 'option', this.employeeOptions);
            this.$set(this.options.filters[4], 'option', this.departmentOptions);
        },
        handleTableSuccess(response) {
            const shifts = response?.data?.data || [];

            if (!this.selectedShiftId) {
                return;
            }

            const refreshed = shifts.find(shift => shift.id === this.selectedShiftId);
            if (refreshed) {
                this.selectedShiftRecord = refreshed;
                return;
            }

            this.selectedShiftId = null;
            this.selectedShiftRecord = null;
            this.assignmentOpen = false;
            this.assignmentForm = this.getInitialAssignmentForm();
        },
        handleTableError(response) {
            this.$toastr.e(this.$resolveError({ response }, { key: 'failed_response' }));
        },
        refreshTable() {
            this.$hub.$emit('reload-' + this.tableId);
        },
        getListAction(rowData, actionObj) {
            this.selectShift(rowData);

            if (actionObj.key === 'edit') {
                this.openEditShift(rowData);
                return;
            }

            if (actionObj.key === 'assign') {
                this.openAssignEmployees(rowData);
            }
        },
        selectShift(shift) {
            this.selectedShiftId = shift.id;
            this.selectedShiftRecord = shift;
            this.assignmentErrors = {};
            this.assignmentForm = this.getInitialAssignmentForm(shift.start_date || null);
        },
        openAssignEmployees(shift = null) {
            const target = shift || this.selectedShiftRecord;

            if (!target || !this.canAssignEmployees) {
                return;
            }

            this.editorOpen = false;
            this.selectShift(target);
            this.assignmentOpen = true;
            this.$nextTick(() => this.initializeTooltip(0));
        },
        closeAssignmentDrawer() {
            this.assignmentOpen = false;
            this.assignmentErrors = {};
        },
        startCreatingShift() {
            this.assignmentOpen = false;
            this.editorOpen = true;
            this.editorShiftId = null;
            this.formLoading = false;
            this.saveLoading = false;
            this.formErrors = {};
            this.shiftForm = this.getInitialShiftForm();
            this.scheduledTemplate = this.getInitialScheduledTemplate();
            this.$nextTick(() => this.initializeTooltip(0));
        },
        openEditShift(shift = null) {
            const target = shift || this.selectedShiftRecord;

            if (!target || !this.canUpdateShifts) {
                return;
            }

            this.assignmentOpen = false;
            this.editorOpen = true;
            this.editorShiftId = target.id;
            this.formLoading = false;
            this.saveLoading = false;
            this.formErrors = {};
            this.shiftForm = this.normalizeShiftForForm(target);
            this.syncScheduledTemplateFromShiftForm();
            this.$nextTick(() => this.initializeTooltip(0));
        },
        closeEditor() {
            this.editorOpen = false;
            this.editorShiftId = null;
            this.formErrors = {};
            this.formLoading = false;
            this.saveLoading = false;
            this.shiftForm = this.getInitialShiftForm();
            this.scheduledTemplate = this.getInitialScheduledTemplate();
        },
        getInitialShiftForm() {
            return {
                id: null,
                name: '',
                type: 'regular',
                location_id: null,
                start_date: new Date(),
                end_date: '',
                start_at: '09:00',
                end_at: '17:00',
                break_minutes: 60,
                grace_minutes: 15,
                weekend_days: defaultRegularWeekends(),
                is_active: true,
                details: defaultScheduledDetails(),
            };
        },
        getInitialScheduledTemplate() {
            return {
                start_at: '09:00',
                end_at: '17:00',
                break_minutes: 60,
                grace_minutes: 15,
            };
        },
        getInitialAssignmentForm(effectiveFrom = null) {
            return {
                employee_ids: [],
                effective_from: effectiveFrom ? new Date(effectiveFrom) : new Date(),
                notes: '',
                sync_default_location: true,
            };
        },
        setShiftType(type) {
            if (!this.canEditCurrentShift) {
                return;
            }

            this.shiftForm.type = type;
        },
        setDetailWorkingState(index, isWorking) {
            this.toggleDetailWeekend(index, !isWorking);
        },
        toggleDetailWorkingDay(index, checked) {
            this.toggleDetailWeekend(index, !checked);
        },
        toggleDetailWeekend(index, checked) {
            const detail = this.shiftForm.details[index];
            if (!detail) {
                return;
            }

            detail.is_weekend = checked;
            if (checked) {
                detail.start_at = '';
                detail.end_at = '';
                detail.break_minutes = 0;
                detail.grace_minutes = 0;
                return;
            }

            detail.start_at = detail.start_at || this.scheduledTemplate.start_at || '09:00';
            detail.end_at = detail.end_at || this.scheduledTemplate.end_at || '17:00';
            detail.break_minutes = detail.break_minutes || Number(this.scheduledTemplate.break_minutes || 60);
            detail.grace_minutes = detail.grace_minutes || Number(this.scheduledTemplate.grace_minutes || 15);
        },
        applyScheduledTemplateToWorkingDays() {
            if (!this.canEditCurrentShift || !Array.isArray(this.shiftForm.details)) {
                return;
            }

            this.shiftForm.details = this.shiftForm.details.map(detail => {
                if (detail.is_weekend) {
                    return detail;
                }

                return {
                    ...detail,
                    start_at: this.scheduledTemplate.start_at || '',
                    end_at: this.scheduledTemplate.end_at || '',
                    break_minutes: Number(this.scheduledTemplate.break_minutes || 0),
                    grace_minutes: Number(this.scheduledTemplate.grace_minutes || 0),
                };
            });
        },
        resetScheduledDetails() {
            if (!this.canEditCurrentShift) {
                return;
            }

            this.shiftForm.details = defaultScheduledDetails();
            this.scheduledTemplate = this.getInitialScheduledTemplate();
        },
        syncScheduledTemplateFromShiftForm() {
            const workingDetail = Array.isArray(this.shiftForm.details)
                ? this.shiftForm.details.find(detail => !detail.is_weekend)
                : null;

            if (!workingDetail) {
                this.scheduledTemplate = this.getInitialScheduledTemplate();
                return;
            }

            this.scheduledTemplate = {
                start_at: workingDetail.start_at || '09:00',
                end_at: workingDetail.end_at || '17:00',
                break_minutes: Number(workingDetail.break_minutes ?? 60),
                grace_minutes: Number(workingDetail.grace_minutes ?? 15),
            };
        },
        normalizeShiftForForm(shift) {
            const details = Array.isArray(shift.details) && shift.details.length
                ? shift.details
                : defaultScheduledDetails();
            const firstWorkingDay = details.find(detail => !detail.is_weekend) || details[0] || {};

            return {
                id: shift.id,
                name: shift.name || '',
                type: shift.type || 'regular',
                location_id: shift.location_id || shift.location?.id || null,
                start_date: shift.start_date ? new Date(shift.start_date) : new Date(),
                end_date: shift.end_date ? new Date(shift.end_date) : '',
                start_at: firstWorkingDay.start_at ? this.normalizeTimeValue(firstWorkingDay.start_at) : '',
                end_at: firstWorkingDay.end_at ? this.normalizeTimeValue(firstWorkingDay.end_at) : '',
                break_minutes: shift.break_minutes ?? firstWorkingDay.break_minutes ?? 60,
                grace_minutes: shift.grace_minutes ?? firstWorkingDay.grace_minutes ?? firstWorkingDay.grace_time_before ?? 15,
                weekend_days: details.filter(detail => !!detail.is_weekend).map(detail => detail.weekday),
                is_active: shift.is_active !== false,
                details: details.map(detail => ({
                    weekday: detail.weekday,
                    start_at: detail.start_at ? this.normalizeTimeValue(detail.start_at) : '',
                    end_at: detail.end_at ? this.normalizeTimeValue(detail.end_at) : '',
                    break_minutes: detail.break_minutes ?? 0,
                    grace_minutes: detail.grace_minutes ?? detail.grace_time_before ?? 0,
                    is_weekend: !!detail.is_weekend,
                })),
            };
        },
        normalizeTimeValue(value) {
            if (!value) {
                return '';
            }

            return moment(value, ['HH:mm:ss', 'HH:mm']).format('HH:mm');
        },
        buildShiftPayload() {
            const payload = {
                name: this.shiftForm.name,
                type: this.shiftForm.type,
                location_id: this.shiftForm.location_id || null,
                start_date: this.formatDateForApi(this.shiftForm.start_date),
                end_date: this.formatDateForApi(this.shiftForm.end_date),
                break_minutes: Number(this.shiftForm.break_minutes || 0),
                grace_minutes: Number(this.shiftForm.grace_minutes || 0),
                is_active: !!this.shiftForm.is_active,
            };

            if (this.shiftForm.type === 'regular') {
                payload.start_at = this.shiftForm.start_at || null;
                payload.end_at = this.shiftForm.end_at || null;
                payload.details = defaultWeekdayDefinitions().map(day => {
                    const isWeekend = this.shiftForm.weekend_days.includes(day.weekday);
                    return {
                        weekday: day.weekday,
                        is_weekend: isWeekend,
                        start_at: isWeekend ? null : (this.shiftForm.start_at || null),
                        end_at: isWeekend ? null : (this.shiftForm.end_at || null),
                        break_minutes: isWeekend ? 0 : Number(this.shiftForm.break_minutes || 0),
                        grace_time_before: isWeekend ? 0 : Number(this.shiftForm.grace_minutes || 0),
                        grace_time_after: isWeekend ? 0 : Number(this.shiftForm.grace_minutes || 0),
                    };
                });
                return payload;
            }

            payload.details = this.shiftForm.details.map(detail => ({
                weekday: detail.weekday,
                is_weekend: !!detail.is_weekend,
                start_at: detail.is_weekend ? null : (detail.start_at || null),
                end_at: detail.is_weekend ? null : (detail.end_at || null),
                break_minutes: detail.is_weekend ? 0 : Number(detail.break_minutes || 0),
                grace_time_before: detail.is_weekend ? 0 : Number(detail.grace_minutes || 0),
                grace_time_after: detail.is_weekend ? 0 : Number(detail.grace_minutes || 0),
            }));

            return payload;
        },
        async submitShift() {
            this.saveLoading = true;
            this.formErrors = {};
            const isUpdating = !!this.editorShiftId;

            try {
                const payload = this.buildShiftPayload();
                const response = isUpdating
                    ? await this.axiosPut({ url: `${SHIFT_SCHEDULING}/${this.editorShiftId}`, data: payload })
                    : await this.axiosPost({ url: SHIFT_SCHEDULING, data: payload });

                const shift = response?.data?.data?.shift;
                if (shift) {
                    this.selectShift(shift);
                }

                this.closeEditor();
                this.$toastr.s(
                    response?.data?.message
                    || (isUpdating ? this.$t('shift_updated_successfully') : this.$t('shift_created_successfully'))
                );
                this.refreshTable();
            } catch (error) {
                this.formErrors = error?.response?.data?.errors || {};
                this.$toastr.e(this.$resolveError(error, { key: 'failed_response' }));
            } finally {
                this.saveLoading = false;
            }
        },
        async assignEmployeesToShift() {
            if (!this.selectedShiftId) {
                return;
            }

            this.assignmentLoading = true;
            this.assignmentErrors = {};

            try {
                const response = await this.axiosPost({
                    url: `${SHIFT_SCHEDULING}/${this.selectedShiftId}/assign`,
                    data: {
                        employee_ids: this.assignmentForm.employee_ids,
                        effective_from: this.formatDateForApi(this.assignmentForm.effective_from),
                        notes: this.assignmentForm.notes,
                        sync_default_location: this.assignmentForm.sync_default_location,
                    },
                });

                const shift = response?.data?.data?.shift;
                if (shift) {
                    this.selectShift(shift);
                }
                this.assignmentForm = this.getInitialAssignmentForm(this.selectedShiftRecord?.start_date || null);
                this.$toastr.s(response?.data?.message || this.$t('shift_assignment_created_successfully'));
                this.refreshTable();
            } catch (error) {
                this.assignmentErrors = error?.response?.data?.errors || {};
                this.$toastr.e(this.$resolveError(error, { key: 'failed_response' }));
            } finally {
                this.assignmentLoading = false;
            }
        },
        shiftTypeLabel(shift) {
            return shift?.type === 'scheduled' ? this.$t('scheduled') : this.$t('regular');
        },
        shiftEmployeeCount(shift) {
            return Math.max(
                Number(shift?.employees_count || 0),
                Number(shift?.employee_assignments_count || 0)
            );
        },
        shiftWorkingDaysCount(shift) {
            if (!Array.isArray(shift?.details)) {
                return 0;
            }

            return shift.details.filter(detail => !detail.is_weekend).length;
        },
        shiftTimeSummary(shift) {
            const workingDetail = Array.isArray(shift.details)
                ? shift.details.find(detail => !detail.is_weekend)
                : null;

            if (workingDetail?.start_at && workingDetail?.end_at) {
                return `${this.normalizeTimeValue(workingDetail.start_at)} - ${this.normalizeTimeValue(workingDetail.end_at)}`;
            }

            return this.shiftTypeLabel(shift);
        },
        formatDateLabel(date) {
            if (!date) {
                return this.$t('not_available');
            }

            return moment(date).format('DD MMM YYYY');
        },
        formatDateRange(startDate, endDate) {
            if (!startDate && !endDate) {
                return this.$t('not_available');
            }

            const start = startDate ? this.formatDateLabel(startDate) : this.$t('not_available');
            const end = endDate ? this.formatDateLabel(endDate) : this.$t('not_available');

            if (!endDate) {
                return start;
            }

            return `${start} - ${end}`;
        },
        formatDateForApi(date) {
            if (!date) {
                return null;
            }

            return moment(date).format('YYYY-MM-DD');
        },
        fieldError(field) {
            const value = this.formErrors?.[field];
            return Array.isArray(value) ? value[0] : '';
        },
        detailFieldError(index, field) {
            const value = this.formErrors?.[`details.${index}.${field}`];
            return Array.isArray(value) ? value[0] : '';
        },
        assignmentFieldError(field) {
            const value = this.assignmentErrors?.[field];
            return Array.isArray(value) ? value[0] : '';
        },
        escapeHtml(value) {
            return `${value || ''}`
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        },
    },
};
</script>

<style scoped>
.shifts-scheduling-page__intro {
    max-width: 640px;
    color: var(--sidebar-text-muted);
}

.shifts-scheduling-page__top-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.shifts-scheduling-page__table-card-body {
    padding: 1.35rem 1.25rem 1.25rem;
}

.shifts-scheduling-page__filters-card,
.shifts-scheduling-page__calendar-card,
.shifts-scheduling-page__library-card,
.shifts-scheduling-page__summary-card,
.shifts-scheduling-page__editor-card,
.shifts-scheduling-page__assignment-card {
    border-radius: 18px;
    overflow: hidden;
}

.shifts-scheduling-page__label {
    display: inline-block;
    margin-bottom: 0.45rem;
    font-size: 0.83rem;
    font-weight: 600;
    color: var(--default-font-color);
}

.shifts-scheduling-page__help-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 0.4rem;
    color: var(--sidebar-text-muted);
    cursor: help;
}

.shifts-scheduling-page__help-trigger ::v-deep svg,
.shifts-scheduling-page__help-inline ::v-deep svg {
    width: 14px;
    height: 14px;
}

.shifts-scheduling-page__help-inline {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: var(--sidebar-text-muted);
    font-size: 0.76rem;
    cursor: help;
}

.shifts-scheduling-page__calendar-head,
.shifts-scheduling-page__calendar-wrap,
.shifts-scheduling-page__shift-grid,
.shifts-scheduling-page__side-stack,
.shifts-scheduling-page__summary-meta-grid,
.shifts-scheduling-page__employee-stack,
.shifts-scheduling-page__toggle-row {
    display: grid;
}

.shifts-scheduling-page__calendar-head {
    gap: 1rem;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
}

.shifts-scheduling-page__legend {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.75rem;
}

.shifts-scheduling-page__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.78rem;
    color: var(--sidebar-text-muted);
}

.shifts-scheduling-page__legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
}

.shifts-scheduling-page__legend-dot--scheduled {
    background: #1d4ed8;
}

.shifts-scheduling-page__legend-dot--override {
    background: #d97706;
}

.shifts-scheduling-page__legend-dot--dayoff {
    background: #6b7280;
}

.shifts-scheduling-page__calendar-body {
    position: relative;
}

.shifts-scheduling-page__calendar-wrap,
.shifts-scheduling-page__panel-loading {
    position: relative;
}

.shifts-scheduling-page__calendar-loading,
.shifts-scheduling-page__panel-loading {
    position: absolute;
    inset: 0;
    z-index: 4;
    background: rgba(255, 255, 255, 0.68);
    border-radius: 16px;
}

.shifts-scheduling-page__shift-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
}

.shifts-scheduling-page__shift-card {
    border: 1px solid var(--default-border-color);
    border-radius: 16px;
    background: var(--default-card-bg);
    padding: 1rem;
    text-align: left;
    transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.shifts-scheduling-page__shift-card:hover,
.shifts-scheduling-page__shift-card.is-active {
    transform: translateY(-2px);
    border-color: rgba(29, 78, 216, 0.28);
    box-shadow: 0 18px 32px rgba(15, 23, 42, 0.08);
}

.shifts-scheduling-page__shift-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.9rem;
}

.shifts-scheduling-page__shift-card-top h6 {
    margin-bottom: 0.2rem;
    font-size: 0.97rem;
    font-weight: 600;
    color: var(--default-font-color);
}

.shifts-scheduling-page__shift-card-top p,
.shifts-scheduling-page__employees-meta {
    margin-bottom: 0;
    font-size: 0.8rem;
    color: var(--sidebar-text-muted);
}

.shifts-scheduling-page__shift-card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
}

.shifts-scheduling-page__location-chip {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-height: 32px;
    padding: 0.4rem 0.75rem;
    border-radius: 999px;
    background: rgba(37, 99, 235, 0.08);
    color: #1d4ed8;
    font-size: 0.78rem;
    font-weight: 600;
}

.shifts-scheduling-page__status-badge {
    white-space: nowrap;
}

.shifts-scheduling-page__side-stack {
    gap: 1rem;
    position: sticky;
    top: 1.25rem;
}

.shifts-scheduling-page__summary-card {
    background: linear-gradient(145deg, rgba(29, 78, 216, 0.08), rgba(14, 165, 233, 0.06));
}

.shifts-scheduling-page__summary-symbol {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.85rem;
    background: rgba(29, 78, 216, 0.14);
    color: #1d4ed8;
    font-size: 1rem;
    font-weight: 700;
}

.shifts-scheduling-page__summary-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-end;
}

.shifts-scheduling-page__summary-meta-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

.shifts-scheduling-page__summary-meta-grid small {
    display: block;
    margin-bottom: 0.15rem;
    color: var(--sidebar-text-muted);
}

.shifts-scheduling-page__summary-meta-grid strong {
    color: var(--default-font-color);
    font-size: 0.88rem;
}

.shifts-scheduling-page__toggle-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
}

.shifts-scheduling-page__toggle-btn {
    border: 1px solid var(--default-border-color);
    border-radius: 14px;
    background: var(--default-card-bg);
    padding: 0.9rem;
    text-align: left;
    transition: all 0.2s ease;
}

.shifts-scheduling-page__toggle-btn span {
    display: block;
    font-weight: 600;
    color: var(--default-font-color);
}

.shifts-scheduling-page__toggle-btn small {
    display: block;
    margin-top: 0.2rem;
    color: var(--sidebar-text-muted);
    font-size: 0.75rem;
}

.shifts-scheduling-page__toggle-btn.is-active {
    border-color: rgba(29, 78, 216, 0.32);
    background: rgba(29, 78, 216, 0.05);
}

.shifts-scheduling-page__scheduled-grid {
    margin-bottom: -0.9rem;
}

.shifts-scheduling-page__scheduled-grid-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.shifts-scheduling-page__scheduled-tools {
    border: 1px solid var(--default-border-color);
    border-radius: 16px;
    background: rgba(29, 78, 216, 0.04);
    padding: 0.95rem;
}

.shifts-scheduling-page__scheduled-tools-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
}

.shifts-scheduling-page__scheduled-tools-hint {
    display: flex;
    flex-wrap: wrap;
    gap: 0.85rem;
    margin-top: 0.75rem;
}

.shifts-scheduling-page__scheduled-tools-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 0.75rem;
}

.shifts-scheduling-page__scheduled-day {
    border: 1px solid var(--default-border-color);
    border-radius: 16px;
    background: var(--default-card-bg);
    padding: 0.95rem;
    width: 100%;
    height: 100%;
}

.shifts-scheduling-page__scheduled-day-caption {
    font-size: 0.78rem;
}

.shifts-scheduling-page__scheduled-day-col {
    display: flex;
}

.shifts-scheduling-page__day-state-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.45rem;
    flex-wrap: wrap;
}

.shifts-scheduling-page__day-state-toggle .btn {
    min-width: 108px;
    border-radius: 999px;
}

.shifts-scheduling-page__scheduled-day-help {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.1rem;
}

.shifts-scheduling-page__switches {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
}

.shifts-scheduling-page__editor-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
}

.shifts-scheduling-page__current-employees {
    border-top: 1px solid var(--default-border-color);
    padding-top: 1rem;
}

.shifts-scheduling-page__employee-stack {
    gap: 0.75rem;
}

.shifts-scheduling-page__employee-row {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 0.75rem;
    align-items: center;
}

.shifts-scheduling-page__employee-row strong,
.shifts-scheduling-page__employee-row small {
    display: block;
}

.shifts-scheduling-page__employee-row small {
    color: var(--sidebar-text-muted);
}

.shifts-scheduling-page__employee-avatar {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(29, 78, 216, 0.12);
    color: #1d4ed8;
    font-size: 0.8rem;
    font-weight: 700;
}

.shifts-scheduling-page__empty-state {
    min-height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 0.5rem;
}

.shifts-scheduling-page__empty-icon {
    width: 62px;
    height: 62px;
    border-radius: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(29, 78, 216, 0.08);
    color: #1d4ed8;
    font-size: 1.2rem;
    font-weight: 700;
}

.shifts-scheduling-page__empty-inline {
    padding: 0.85rem 1rem;
    border-radius: 14px;
    background: rgba(148, 163, 184, 0.12);
    color: var(--sidebar-text-muted);
    font-size: 0.82rem;
}

.shifts-scheduling-page__count-pill {
    font-size: 0.78rem;
    font-weight: 600;
}

.shifts-scheduling-page__editor-shell {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    justify-content: flex-end;
}

.shifts-scheduling-page__editor-backdrop {
    position: absolute;
    inset: 0;
    border: 0;
    background: rgba(15, 23, 42, 0.42);
}

.shifts-scheduling-page__editor-drawer {
    position: relative;
    z-index: 1;
    width: min(620px, 100%);
    height: 100%;
    background: var(--default-card-bg);
    box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
    display: flex;
    flex-direction: column;
}

.shifts-scheduling-page__editor-drawer-header {
    position: sticky;
    top: 0;
    z-index: 3;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.35rem 1.35rem 1rem;
    background: var(--default-card-bg);
    border-bottom: 1px solid var(--default-border-color);
}

.shifts-scheduling-page__editor-drawer-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 1.35rem;
}

.shift-editor-fade-enter-active,
.shift-editor-fade-leave-active {
    transition: opacity 0.18s ease;
}

.shift-editor-fade-enter,
.shift-editor-fade-leave-to {
    opacity: 0;
}

::v-deep .fc {
    --fc-border-color: var(--default-border-color);
    --fc-page-bg-color: transparent;
    --fc-neutral-bg-color: rgba(148, 163, 184, 0.08);
    --fc-list-event-hover-bg-color: rgba(29, 78, 216, 0.06);
    --fc-today-bg-color: rgba(29, 78, 216, 0.06);
    color: var(--default-font-color);
}

::v-deep .fc .fc-toolbar {
    flex-wrap: wrap;
    gap: 0.85rem;
}

::v-deep .fc .fc-toolbar-title {
    font-size: 1.1rem;
    font-weight: 600;
}

::v-deep .fc .fc-button {
    border-radius: 12px;
    border: 0;
    box-shadow: none !important;
    background: rgba(15, 23, 42, 0.08);
    color: var(--default-font-color);
    text-transform: capitalize;
}

::v-deep .fc .fc-button-primary:not(:disabled).fc-button-active,
::v-deep .fc .fc-button-primary:not(:disabled):active,
::v-deep .fc .fc-button-primary:not(:disabled):hover {
    background: #1d4ed8;
    color: #fff;
}

::v-deep .fc .fc-daygrid-day-frame,
::v-deep .fc .fc-timegrid-slot,
::v-deep .fc .fc-timegrid-axis,
::v-deep .fc .fc-col-header-cell {
    background: transparent;
}

::v-deep .fc .fc-event {
    border: 0;
    padding: 0;
    box-shadow: none;
    background: transparent;
}

::v-deep .scheduler-event-card {
    border-radius: 12px;
    padding: 0.45rem 0.55rem;
    background: linear-gradient(145deg, #1d4ed8, #2563eb);
    color: #fff;
    min-height: 100%;
}

::v-deep .is-override .scheduler-event-card {
    background: linear-gradient(145deg, #d97706, #f59e0b);
}

::v-deep .is-day_off .scheduler-event-card,
::v-deep .scheduler-event-card--dayoff {
    background: linear-gradient(145deg, #475569, #64748b);
}

::v-deep .scheduler-event-card__time {
    font-size: 0.7rem;
    opacity: 0.9;
    margin-bottom: 0.15rem;
}

::v-deep .scheduler-event-card__title {
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.2;
}

::v-deep .scheduler-event-card__meta {
    font-size: 0.7rem;
    opacity: 0.94;
    line-height: 1.2;
    margin-top: 0.15rem;
}

::v-deep .scheduler-event-card__chip {
    display: inline-flex;
    align-items: center;
    margin-top: 0.35rem;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.16);
    font-size: 0.66rem;
    font-weight: 600;
}

@media (max-width: 1199px) {
    .shifts-scheduling-page__side-stack {
        position: static;
    }
}

@media (max-width: 991px) {
    .shifts-scheduling-page__calendar-head {
        grid-template-columns: 1fr;
    }

    .shifts-scheduling-page__legend {
        justify-content: flex-start;
    }
}

@media (max-width: 767px) {
    .shifts-scheduling-page__top-actions,
    .shifts-scheduling-page__editor-actions {
        justify-content: stretch;
    }

    .shifts-scheduling-page__top-actions .btn,
    .shifts-scheduling-page__editor-actions .btn {
        width: 100%;
    }

    .shifts-scheduling-page__summary-meta-grid,
    .shifts-scheduling-page__toggle-row {
        grid-template-columns: 1fr;
    }

    .shifts-scheduling-page__scheduled-tools-grid {
        grid-template-columns: 1fr;
    }

    .shifts-scheduling-page__scheduled-grid-head,
    .shifts-scheduling-page__scheduled-tools-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .shifts-scheduling-page__scheduled-day-col {
        flex: 0 0 100%;
        max-width: 100%;
    }

    .shifts-scheduling-page__summary-actions {
        width: 100%;
        align-items: stretch;
    }

    .shifts-scheduling-page__summary-actions .btn {
        width: 100%;
    }

    .shifts-scheduling-page__editor-drawer {
        width: 100%;
    }
}
</style>
